import React from 'react';
import { Card, Tag, Progress, Button, Descriptions, Alert, Row, Col, Statistic } from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  CloseCircleOutlined,
  BulbOutlined,
  WarningOutlined
} from '@ant-design/icons';

const AIEligibilityDisplay = ({ eligibility, onRecheck, loading }) => {
  if (!eligibility) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <BulbOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: 16 }} />
        <div style={{ color: '#999', fontSize: '16px' }}>
          No eligibility assessment available yet.
        </div>
        <div style={{ color: '#666', fontSize: '14px', marginTop: 8 }}>
          Click "Recheck Eligibility" to perform an AI assessment.
        </div>
      </div>
    );
  }

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'APPROVE': return 'success';
      case 'CONDITIONAL_APPROVE': return 'warning';
      case 'DECLINE': return 'error';
      default: return 'default';
    }
  };

  const getRecommendationIcon = (recommendation) => {
    switch (recommendation) {
      case 'APPROVE': return <CheckCircleOutlined />;
      case 'CONDITIONAL_APPROVE': return <ExclamationCircleOutlined />;
      case 'DECLINE': return <CloseCircleOutlined />;
      default: return <BulbOutlined />;
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'LOW': return 'green';
      case 'MEDIUM': return 'orange';
      case 'HIGH': return 'red';
      default: return 'default';
    }
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Main Recommendation */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card bordered={false} style={{ textAlign: 'center' }}>
            <Statistic
              title="Recommendation"
              value={eligibility.recommendation.replace('_', ' ')}
              prefix={getRecommendationIcon(eligibility.recommendation)}
              valueStyle={{ 
                color: getRecommendationColor(eligibility.recommendation) === 'success' ? '#52c41a' :
                       getRecommendationColor(eligibility.recommendation) === 'warning' ? '#faad14' : '#ff4d4f'
              }}
            />
          </Card>
        </Col>
        
        <Col span={8}>
          <Card bordered={false} style={{ textAlign: 'center' }}>
            <Progress
              type="dashboard"
              percent={eligibility.confidence}
              format={() => `${eligibility.confidence}%`}
              strokeColor={eligibility.confidence >= 80 ? '#52c41a' : eligibility.confidence >= 60 ? '#faad14' : '#ff4d4f'}
              width={80}
            />
            <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
              Confidence
            </div>
          </Card>
        </Col>
        
        <Col span={8}>
          <Card bordered={false} style={{ textAlign: 'center' }}>
            <Statistic
              title="Risk Level"
              value={eligibility.riskLevel}
              prefix={<WarningOutlined />}
              valueStyle={{ 
                color: getRiskColor(eligibility.riskLevel) === 'green' ? '#52c41a' :
                       getRiskColor(eligibility.riskLevel) === 'orange' ? '#faad14' : '#ff4d4f'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Descriptions title="Loan Details" bordered size="small">
            <Descriptions.Item label="Max Loan Amount" span={3}>
              ZMW {eligibility.maxLoanAmount?.toLocaleString() || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Suggested Rate" span={3}>
              {eligibility.suggestedInterestRate || 'N/A'}% per month
            </Descriptions.Item>
            {eligibility.traditionalScore && (
              <Descriptions.Item label="Credit Score" span={3}>
                {eligibility.traditionalScore}/850
              </Descriptions.Item>
            )}
          </Descriptions>
        </Col>
        
        <Col span={12}>
          {/* Assessment Breakdown */}
          {eligibility.combinedAnalysis && (
            <Card size="small" title="Assessment Breakdown">
              <div style={{ fontSize: '12px' }}>
                <div>AI Weight: {eligibility.assessmentDetails?.aiWeight}%</div>
                <div>Traditional Weight: {eligibility.assessmentDetails?.traditionalWeight}%</div>
                <div style={{ marginTop: 8 }}>
                  <Tag color="blue" size="small">AI: {eligibility.assessmentDetails?.aiRecommendation}</Tag>
                  <Tag color="default" size="small">Traditional: {eligibility.assessmentDetails?.traditionalRecommendation}</Tag>
                </div>
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Conditions */}
      {eligibility.conditions && eligibility.conditions.length > 0 && (
        <Alert
          message="Conditions Required"
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {eligibility.conditions.map((condition, index) => (
                <li key={index}>{condition}</li>
              ))}
            </ul>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Risk and Positive Factors */}
      <Row gutter={16}>
        {eligibility.riskFactors && eligibility.riskFactors.length > 0 && (
          <Col span={12}>
            <Card size="small" title="Risk Factors" style={{ height: '100%' }}>
              {eligibility.riskFactors.map((factor, index) => (
                <Tag key={index} color="red" style={{ marginBottom: 4 }}>
                  {factor}
                </Tag>
              ))}
            </Card>
          </Col>
        )}
        
        {eligibility.positiveFactors && eligibility.positiveFactors.length > 0 && (
          <Col span={12}>
            <Card size="small" title="Positive Factors" style={{ height: '100%' }}>
              {eligibility.positiveFactors.map((factor, index) => (
                <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                  {factor}
                </Tag>
              ))}
            </Card>
          </Col>
        )}
      </Row>

      {/* AI Reasoning */}
      {eligibility.reasoning && (
        <Card 
          size="small" 
          title="AI Analysis" 
          style={{ marginTop: 16 }}
          bodyStyle={{ maxHeight: '100px', overflow: 'auto' }}
        >
          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
            {eligibility.aiInsights || eligibility.reasoning}
          </div>
        </Card>
      )}
    </div>
  );
};

export default AIEligibilityDisplay;